// Loaded exclusively by the CarPlay purchase block. No changes to the cart flow.
class CarplayPurchaseHelp extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.panel = this.querySelector('[data-contact-panel]');
    this.popup = this.querySelector('[data-popup]');
    this.opener = this.querySelector('[data-open]');
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.storageKey = `carplay-help-shown-${this.dataset.productId}`;
    this.checkoutKey = `carplay-help-checkout-${this.dataset.productId}`;
    this.elapsed = 0;
    this.lastTick = Date.now();
    this.cartTriggerAt = this.dataset.inCart === 'true' ? 15000 : Infinity;
    this.autoShown = false;
    this.checkoutStarted = false;
    try {
      this.autoShown = sessionStorage.getItem(this.storageKey) === 'true';
      this.checkoutStarted = sessionStorage.getItem(this.checkoutKey) === 'true';
      sessionStorage.setItem(this.storageKey, String(this.autoShown));
    } catch {
      // Without session storage, keep manual contact available and avoid repeat prompts.
      this.autoShown = true;
    }
    this.addEventListener('click', this.onClick, { signal });
    this.querySelector('form').addEventListener('submit', () => this.updateProductContext(), { signal });
    document.addEventListener('shopify:cart:lines-update', this.onCartUpdate, { signal });
    document.addEventListener('visibilitychange', this.onVisibilityChange, { signal });
    document.addEventListener('click', this.onDocumentClick, { signal, capture: true });
    document.addEventListener('submit', this.onCheckoutSubmit, { signal, capture: true });
    document.addEventListener('keydown', this.onKeyDown, { signal });
    document.documentElement.addEventListener('mouseleave', this.onExitIntent, { signal });

    if (this.querySelector('[data-form-result]')) {
      this.openPopup(true);
      this.panel.hidden = false;
    }
    clearInterval(this.timer);
    this.timer = setInterval(this.tick, 1000);
    this.refreshCart();
  }

  disconnectedCallback() {
    this.controller.abort();
    clearInterval(this.timer);
    if (this.popup.hidePopover && this.popup.matches(':popover-open')) this.popup.hidePopover();
  }

  openPopup(manual = false) {
    if (!manual && (this.autoShown || this.checkoutStarted || document.hidden ||
        Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some((element) => element.getClientRects().length) ||
        document.activeElement?.matches('input, textarea, select, [contenteditable="true"]'))) return;
    if (!this.popup.hidden) return;
    this.autoShown = true;
    try { sessionStorage.setItem(this.storageKey, 'true'); } catch { /* Manual access still works. */ }
    this.popup.hidden = false;
    this.popup.showPopover?.();
    this.opener.setAttribute('aria-expanded', 'true');
    this.updateProductContext();
    this.refreshCart();
    // Automatic prompts never move focus away from the buying controls.
    if (manual) this.querySelector('[data-close]').focus({ preventScroll: true });
  }

  closePopup() {
    const restoreFocus = this.popup.contains(document.activeElement);
    if (this.popup.hidePopover && this.popup.matches(':popover-open')) this.popup.hidePopover();
    this.popup.hidden = true;
    this.opener.setAttribute('aria-expanded', 'false');
    if (restoreFocus) {
      const checker = this.closest('.buy-buttons-block')?.querySelector('carplay-compatibility-checker[data-has-result]');
      (checker?.querySelector('[data-fit-contact]') || this.opener).focus({ preventScroll: true });
    }
  }

  tick = () => {
    const now = Date.now();
    if (!document.hidden) this.elapsed += now - this.lastTick;
    this.lastTick = now;
    if (this.elapsed >= (Number.isFinite(this.cartTriggerAt) ? this.cartTriggerAt : 30000)) this.openPopup();
  };

  onVisibilityChange = () => { this.lastTick = Date.now(); };

  onExitIntent = (event) => {
    if (event.clientY <= 0 && this.elapsed >= 10000 &&
        window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches) this.openPopup();
  };

  onKeyDown = (event) => {
    if (event.key === 'Escape' && !this.popup.hidden) this.closePopup();
  };

  startCheckout() {
    this.checkoutStarted = true;
    try { sessionStorage.setItem(this.checkoutKey, 'true'); } catch { /* In-memory suppression remains. */ }
    this.closePopup();
  }

  onDocumentClick = (event) => {
    const checkout = event.composedPath().some((node) => node instanceof Element &&
      node.matches('[name="checkout"], a[href*="/checkout"], .shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart'));
    if (checkout) this.startCheckout();
    else if (!this.popup.hidden && !this.contains(event.target)) this.closePopup();
  };

  onCheckoutSubmit = (event) => {
    if (event.submitter?.matches('[name="checkout"]') || event.target.matches('form[action*="/checkout"]')) this.startCheckout();
  };

  onCartUpdate = async (event) => {
    try {
      const result = await event.promise;
      if (!this.isConnected) return;
      const detail = result?.detail;
      if (detail && !detail.didError && detail.source === 'product-form-component' &&
          String(detail.productId) === this.dataset.productId) {
        this.cartTriggerAt = this.elapsed + 15000;
      }
      await this.refreshCart();
    } catch { /* A failed cart request must not interrupt buying or contacting us. */ }
  };

  async refreshCart() {
    try {
      const response = await fetch(this.dataset.cartUrl, { signal: this.controller.signal });
      if (!response.ok) return;
      const cart = await response.json();
      if (!Array.isArray(cart.items)) return;
      if (cart.items.some((item) => String(item.product_id) === this.dataset.productId)) {
        if (!Number.isFinite(this.cartTriggerAt)) this.cartTriggerAt = this.elapsed + 15000;
      } else this.cartTriggerAt = Infinity;
      const field = this.querySelector('[data-cart-context]');
      if (field) field.value = cart.items.length
        ? cart.items.map((item) => `${item.quantity} × ${item.product_title || item.title} — ${item.variant_title || 'Standard'} (variante ${item.variant_id})`).join('\n')
        : 'Panier vide';
    } catch { /* Keep the server-rendered cart context if the network is unavailable. */ }
  }

  updateProductContext() {
    const checker = this.closest('.buy-buttons-block')?.querySelector('carplay-compatibility-checker');
    const motorcycleField = this.querySelector('[data-motorcycle-context]');
    if (motorcycleField && checker) {
      const model = checker.querySelector('[data-motorcycle]')?.value.trim();
      motorcycleField.value = model || 'Non renseignée';
    }
    const variantId = this.closest('.buy-buttons-block')?.querySelector('product-form-component input[name="id"]')?.value;
    const variant = this.variants.find((item) => String(item.id) === variantId);
    const url = new URL(this.dataset.productUrl);
    if (variantId) url.searchParams.set('variant', variantId);
    const urlField = this.querySelector('[data-product-context]');
    const variantField = this.querySelector('[data-variant-context]');
    if (urlField) urlField.value = url.href;
    if (variantField && variant) variantField.value = `${variant.title} (ID ${variant.id})`;
  }

  onClick = (event) => {
    if (event.target.closest('[data-open]')) this.openPopup(true);
    const choice = event.target.closest('[data-reason]');
    if (choice) {
      this.panel.hidden = false;
      const subject = this.querySelector('[data-subject]');
      const label = this.querySelector('[data-subject-label]');
      if (subject) subject.value = `CarPlay moto — ${choice.dataset.reason}`;
      if (label) label.textContent = choice.dataset.reason;
      this.querySelectorAll('[data-reason]').forEach((button) => {
        button.setAttribute('aria-expanded', String(button === choice));
      });
      this.updateProductContext();
      this.refreshCart();
      this.querySelector('input[type="email"]')?.focus({ preventScroll: true });
    }
    if (event.target.closest('[data-close]')) {
      this.closePopup();
    }
  };
}

if (!customElements.get('carplay-purchase-help')) customElements.define('carplay-purchase-help', CarplayPurchaseHelp);
